/**
 * StyleguidePage · /styleguide
 *
 * Internal design-system dashboard. Renders every design token and every
 * reusable component in all its states. Imports the SAME component files
 * the site uses · edit a component and both hot-reload.
 *
 * Hidden from public: noindex, not in sitemap, not linked from Nav.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import tailwindConfig from '../../tailwind.config.js';

// UI primitives
import Button from '../components/ui/Button.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import Tag from '../components/ui/Tag.jsx';
import Check from '../components/ui/Check.jsx';
import Logo from '../components/ui/Logo.jsx';
import SectionHead from '../components/ui/SectionHead.jsx';
import SplitText from '../components/ui/SplitText.jsx';
import Reveal from '../components/ui/Reveal.jsx';

// Primitives
import FAQItem from '../components/primitives/FAQItem.jsx';
import ProcessStep from '../components/primitives/ProcessStep.jsx';
import { MetricCard, Metric } from '../components/primitives/MetricCard.jsx';
import HeroCube from '../components/primitives/HeroCube.jsx';
import TerminalWindow from '../components/primitives/TerminalWindow.jsx';

// New UI primitives (terminal language)
import PromptEyebrow from '../components/ui/PromptEyebrow.jsx';

// Section components
import Hero from '../components/sections/Hero.jsx';
import ForWho from '../components/sections/ForWho.jsx';
import Offers from '../components/sections/Offers.jsx';
import FlagshipBuilds from '../components/sections/FlagshipBuilds.jsx';
import HowWeWork from '../components/sections/HowWeWork.jsx';
import Process from '../components/sections/Process.jsx';
import FAQ from '../components/sections/FAQ.jsx';
import CTA from '../components/sections/CTA.jsx';


// ─── Token extraction from tailwind config ───────────────────────────────────

const tw = tailwindConfig.theme.extend;

/**
 * Flatten a nested color object into "name → value" pairs.
 * e.g. { brand: { blue: '#...' } } → [['brand-blue', '#...']]
 */
function flattenColors(obj, prefix = '') {
  return Object.entries(obj).flatMap(([key, val]) => {
    // DEFAULT is Tailwind's bare-name key: surface.DEFAULT → class "bg-surface"
    const fullKey = key === 'DEFAULT' ? (prefix || key) : prefix ? `${prefix}-${key}` : key;
    if (typeof val === 'string') return [[fullKey, val]];
    return flattenColors(val, fullKey);
  });
}

const allColors = flattenColors(tw.colors);

// Group colours dynamically by first token segment, capitalising the label.
// New colour groups in tokens.js appear automatically without touching this file.
const colorGroups = allColors.reduce((acc, [token, value]) => {
  const segment = token.split('-')[0];
  const label = segment.charAt(0).toUpperCase() + segment.slice(1);
  if (!acc[label]) acc[label] = [];
  acc[label].push([token, value]);
  return acc;
}, {});

const gradients = Object.entries(tw.backgroundImage);
const shadows = Object.entries(tw.boxShadow);
const radii = Object.entries(tw.borderRadius);

// ─── Type scale: token → literal Tailwind class ──────────────────────────────
// Tailwind JIT requires literal class strings in source; no template generation.
const typeScale = [
  { token: 'display-2xl', cls: 'text-display-2xl', family: 'font-display', sample: 'Display 2XL' },
  { token: 'display-xl',  cls: 'text-display-xl',  family: 'font-display', sample: 'Display XL' },
  { token: 'display-lg',  cls: 'text-display-lg',  family: 'font-display', sample: 'Display LG' },
  { token: 'display-md',  cls: 'text-display-md',  family: 'font-display', sample: 'Display MD' },
  { token: 'display-sm',  cls: 'text-display-sm',  family: 'font-display', sample: 'Display SM' },
  { token: 'h3',          cls: 'text-h3',          family: 'font-display', sample: 'Heading H3' },
  { token: 'h4',          cls: 'text-h4',          family: 'font-display', sample: 'Heading H4' },
  { token: 'body-lg',     cls: 'text-body-lg',     family: 'font-sans',    sample: 'Body large. The quick brown fox jumps over the lazy dog.' },
  { token: 'body',        cls: 'text-body',        family: 'font-sans',    sample: 'Body. The quick brown fox jumps over the lazy dog.' },
  { token: 'body-sm',     cls: 'text-body-sm',     family: 'font-sans',    sample: 'Body small. The quick brown fox jumps over the lazy dog.' },
  { token: 'mono-xs',     cls: 'text-mono-xs',     family: 'font-mono',    sample: 'MONO XS · 0.18EM TRACKING' },
  { token: 'mono-sm',     cls: 'text-mono-sm',     family: 'font-mono',    sample: 'MONO SM · 0.14EM TRACKING' },
  { token: 'mono-md',     cls: 'text-mono-md',     family: 'font-mono',    sample: 'Mono MD · 0.05em tracking' },
  { token: 'card-title',  cls: 'text-card-title',  family: 'font-display', sample: 'Card Title Style' },
  { token: 'metric',      cls: 'text-metric',      family: 'font-display', sample: '142h' },
  { token: 'stat',        cls: 'text-stat',        family: 'font-display', sample: '99%' },
];

const fontFamilies = [
  { name: 'display', cls: 'font-display', value: 'Sora, system-ui, sans-serif' },
  { name: 'sans',    cls: 'font-sans',    value: 'Inter, system-ui, sans-serif' },
  { name: 'mono',    cls: 'font-mono',    value: '"JetBrains Mono", ui-monospace, monospace' },
];

// ─── Helper components ────────────────────────────────────────────────────────

function SectionAnchor({ id, label }) {
  return (
    <div id={id} className="pt-8">
      <span className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.18em]">{label}</span>
      <div className="mt-2 h-px bg-white/[.06]" />
    </div>
  );
}

function SpecimenFrame({ label, className = '', children }) {
  return (
    <div className="rounded-lg border border-white/[.08] bg-surface-raised overflow-hidden">
      <div className={`p-6 ${className}`}>
        {children}
      </div>
      {label && (
        <div className="px-4 py-2 border-t border-white/[.06] bg-surface-alt">
          <span className="font-mono text-mono-xs text-ink-300">{label}</span>
        </div>
      )}
    </div>
  );
}

function TokenRow({ label, children }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b border-white/[.04] last:border-0">
      <span className="font-mono text-mono-xs text-ink-300 w-40 shrink-0 pt-0.5">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function SectionDivider({ name, path }) {
  return (
    <div className="flex items-center gap-4 py-3 mt-12 mb-0 border-t border-white/[.08]">
      <span className="font-mono text-mono-xs text-ink-200 uppercase tracking-[0.14em]">{name}</span>
      <span className="font-mono text-mono-xs text-ink-300 ml-2">{path}</span>
    </div>
  );
}

// ─── StyleguidePage ───────────────────────────────────────────────────────────

export default function StyleguidePage() {
  return (
    <>
      <Helmet>
        <title>Styleguide · The Artificer&apos;s AI</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Sticky anchor bar. 84px offset clears the fixed Nav (z-50, py-4 +
          CTA button height) · update if the Nav's height changes. */}
      <div className="sticky top-[84px] z-40 mt-[84px] bg-surface-alt/90 backdrop-blur-glass border-y border-white/[.06]">
        <div className="container-page">
          <nav className="flex items-center gap-6 h-10 overflow-x-auto">
            {[
              ['#tokens',     'Tokens'],
              ['#type',       'Type'],
              ['#components', 'Components'],
              ['#primitives', 'Primitives'],
              ['#terminal',   'Terminal'],
              ['#sections',   'Sections'],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="font-mono text-mono-xs text-ink-300 hover:text-ink-50 transition-colors whitespace-nowrap uppercase tracking-[0.14em]"
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      <div className="bg-surface min-h-screen text-ink-50">
        <div className="container-page py-16">

          {/* Page header */}
          <header className="mb-16 max-w-prose">
            <Eyebrow className="mb-4">Design system</Eyebrow>
            <h1 className="text-display-lg font-display font-semibold mb-4">
              Component and token dashboard
            </h1>
            <p className="text-body-lg text-ink-200">
              This page renders the exact components the site imports. Edit a component file and both this
              page and the live site hot-reload. The tokens below are read live from{' '}
              <code className="font-mono text-mono-sm text-ink-200 bg-white/[.06] px-1.5 py-0.5 rounded">tailwind.config.js</code>.
            </p>
          </header>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TOKENS                                                          */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionAnchor id="tokens" label="Tokens" />

          <div className="mt-8 space-y-12">

            {/* Colour swatches */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Colours</p>
              <div className="space-y-8">
                {Object.entries(colorGroups).map(([group, pairs]) => (
                  <div key={group}>
                    <p className="font-mono text-mono-xs text-ink-300 mb-3">{group}</p>
                    <div className="flex flex-wrap gap-3">
                      {pairs.map(([token, hex]) => (
                        <div key={token} className="flex flex-col gap-1.5 w-28">
                          <div
                            className="h-12 rounded border border-white/[.08]"
                            style={{ background: hex }}
                          />
                          <span className="font-mono text-mono-xs text-ink-200 leading-tight break-words">{token}</span>
                          <span className="font-mono text-mono-xs text-ink-300 leading-tight">{hex}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gradients */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Gradients</p>
              <div className="space-y-2">
                {gradients.map(([token, value]) => (
                  <div key={token} className="flex items-center gap-4">
                    <div
                      className="h-10 rounded flex-1 border border-white/[.06]"
                      style={{ backgroundImage: value }}
                    />
                    <span className="font-mono text-mono-xs text-ink-300 w-52 shrink-0">{token}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Shadows */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Shadows / glows</p>
              <div className="flex flex-wrap gap-4">
                {shadows.map(([token, value]) => (
                  <div key={token} className="flex flex-col items-center gap-2">
                    <div
                      className="w-20 h-14 rounded bg-surface-raised border border-white/[.08]"
                      style={{ boxShadow: value }}
                    />
                    <span className="font-mono text-mono-xs text-ink-300 text-center whitespace-nowrap">{token}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Radii */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Border radii</p>
              <div className="flex flex-wrap gap-6">
                {radii.map(([token, value]) => (
                  <div key={token} className="flex flex-col items-center gap-2">
                    <div
                      className="w-20 h-14 bg-white/[.04] border border-white/[.12]"
                      style={{ borderRadius: value }}
                    />
                    <span className="font-mono text-mono-xs text-ink-200 text-center">{token === 'DEFAULT' ? 'rounded' : `rounded-${token}`}</span>
                    <span className="font-mono text-mono-xs text-ink-300 text-center">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Spacing + max widths */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Spacing tokens</p>
              <div className="space-y-2">
                <TokenRow label="spacing.section">
                  <span className="font-mono text-mono-sm text-ink-200">{tw.spacing.section}</span>
                  <span className="ml-4 text-body-sm text-ink-300">used as py-section via .section-pad</span>
                </TokenRow>
                {Object.entries(tw.maxWidth).map(([k, v]) => (
                  <TokenRow key={k} label={`maxWidth.${k}`}>
                    <span className="font-mono text-mono-sm text-ink-200">{v}</span>
                  </TokenRow>
                ))}
              </div>
            </div>

          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TYPE                                                            */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionAnchor id="type" label="Type" />

          <div className="mt-8 space-y-10">

            {/* Font families */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Font families</p>
              <div className="space-y-3">
                {fontFamilies.map(({ name, cls, value }) => (
                  <SpecimenFrame key={name} label={`fontFamily.${name}  =  ${value}`}>
                    <span className={`${cls} text-h4 text-ink-50`}>
                      The Artificer&apos;s AI · {name}
                    </span>
                  </SpecimenFrame>
                ))}
              </div>
            </div>

            {/* Type scale */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">Type scale</p>
              <div className="space-y-2">
                {typeScale.map(({ token, cls, family, sample }) => {
                  const sizeEntry = tw.fontSize[token];
                  const resolvedSize = Array.isArray(sizeEntry) ? sizeEntry[0] : (sizeEntry || '');
                  return (
                    <SpecimenFrame
                      key={token}
                      label={`text-${token}  =  ${resolvedSize}`}
                    >
                      <span className={`${cls} ${family} text-ink-50`}>{sample}</span>
                    </SpecimenFrame>
                  );
                })}
              </div>
            </div>

          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* COMPONENTS (ui/)                                                */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionAnchor id="components" label="Components" />

          <div className="mt-8 space-y-12">

            {/* Button · full variant × size matrix */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Button · src/components/ui/Button.jsx
              </p>
              <div className="space-y-4">
                {(['primary', 'ghost', 'glass', 'brand'] ).map((variant) => (
                  <SpecimenFrame key={variant} label={`variant="${variant}"`}>
                    <div className="flex flex-wrap items-center gap-4">
                      <Button variant={variant} size="md" as="button">
                        {variant} / md
                      </Button>
                      <Button variant={variant} size="lg" as="button">
                        {variant} / lg
                      </Button>
                      <Button variant={variant} size="md" as="button" disabled className="opacity-40 cursor-not-allowed">
                        disabled
                      </Button>
                    </div>
                  </SpecimenFrame>
                ))}
                <SpecimenFrame label='Focus state · tab to the button to see focus ring'>
                  <Button variant="primary" size="md" as="button">Focus me (tab)</Button>
                </SpecimenFrame>
              </div>
            </div>

            {/* Eyebrow */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Eyebrow · src/components/ui/Eyebrow.jsx
              </p>
              <div className="space-y-3">
                <SpecimenFrame label='tone="default" (default)'>
                  <Eyebrow>The Artificer&apos;s AI</Eyebrow>
                </SpecimenFrame>
                <SpecimenFrame label='tone="purple"'>
                  <Eyebrow tone="purple">Enterprise AI Training</Eyebrow>
                </SpecimenFrame>
                <SpecimenFrame label='as="h2" · renders as any tag'>
                  <Eyebrow as="h2">Section label as h2</Eyebrow>
                </SpecimenFrame>
              </div>
            </div>

            {/* Tag */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Tag · src/components/ui/Tag.jsx
              </p>
              <SpecimenFrame label='<Tag> · mono pill label'>
                <div className="flex flex-wrap gap-2">
                  <Tag>n8n</Tag>
                  <Tag>Make</Tag>
                  <Tag>OpenAI</Tag>
                  <Tag>Zapier</Tag>
                  <Tag>Custom build</Tag>
                </div>
              </SpecimenFrame>
            </div>

            {/* Check */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Check · src/components/ui/Check.jsx
              </p>
              <SpecimenFrame label='<Check /> · circular brand check icon'>
                <div className="flex flex-col gap-3">
                  {['Fully documented handover', 'You own everything, no lock-in', 'Observability from day one'].map((text) => (
                    <div key={text} className="flex items-center gap-3">
                      <Check />
                      <span className="text-body-sm text-ink-200">{text}</span>
                    </div>
                  ))}
                </div>
              </SpecimenFrame>
            </div>

            {/* Logo */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Logo · src/components/ui/Logo.jsx
              </p>
              <div className="space-y-3">
                <SpecimenFrame label='variant="white" showLocator={true} (defaults)'>
                  <Logo href="#" />
                </SpecimenFrame>
                <SpecimenFrame label='variant="white" showLocator={false}'>
                  <Logo href="#" showLocator={false} />
                </SpecimenFrame>
                <SpecimenFrame label='variant="black" · for light backgrounds' className="bg-white">
                  <Logo href="#" variant="black" />
                </SpecimenFrame>
              </div>
            </div>

            {/* SectionHead */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                SectionHead · src/components/ui/SectionHead.jsx
              </p>
              <div className="space-y-3">
                <SpecimenFrame label='maxWidth="default" eyebrowTone="default"'>
                  <SectionHead
                    eyebrow="What we build"
                    heading="Three offers, one outcome"
                    description="Pick the layer that fits where you are. Combine them as you grow."
                  />
                </SpecimenFrame>
                <SpecimenFrame label='maxWidth="narrow" eyebrowTone="purple"'>
                  <SectionHead
                    eyebrow="Enterprise AI Training"
                    eyebrowTone="purple"
                    heading={<>Train your team <span className="text-gradient-accent">to think in systems</span></>}
                    description="Structured enablement programmes for teams ready to go beyond the chatbot."
                    maxWidth="narrow"
                  />
                </SpecimenFrame>
              </div>
            </div>

            {/* SplitText */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                SplitText · src/components/ui/SplitText.jsx
              </p>
              <SpecimenFrame label='text + accent · letter-by-letter reveal on mount'>
                <h2 className="text-display-lg font-display font-semibold">
                  <SplitText text="Build systems" accent="that scale" />
                </h2>
              </SpecimenFrame>
            </div>

            {/* Reveal */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Reveal · src/components/ui/Reveal.jsx
              </p>
              <SpecimenFrame label='<Reveal> · fade-up-on-scroll wrapper; scroll down to see reveal fire'>
                <div className="space-y-4">
                  <Reveal>
                    <div className="p-4 rounded bg-white/[.04] border border-white/[.08] text-body-sm text-ink-200">
                      Reveal delay=0 (default)
                    </div>
                  </Reveal>
                  <Reveal delay={150}>
                    <div className="p-4 rounded bg-white/[.04] border border-white/[.08] text-body-sm text-ink-200">
                      Reveal delay=150ms
                    </div>
                  </Reveal>
                  <Reveal delay={300}>
                    <div className="p-4 rounded bg-white/[.04] border border-white/[.08] text-body-sm text-ink-200">
                      Reveal delay=300ms
                    </div>
                  </Reveal>
                </div>
              </SpecimenFrame>
            </div>

            {/* Shared non-extracted patterns */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Shared CSS patterns (not extracted to components)
              </p>
              <div className="space-y-3">
                <SpecimenFrame label='.glass-card · frosted glass surface'>
                  <div className="glass-card rounded-lg p-6 text-body-sm text-ink-200">
                    Glass card shell · bg-glass-card + border border-white/[.08] + backdrop-blur-glass
                  </div>
                </SpecimenFrame>
                <SpecimenFrame label='Check icon + feature row pattern'>
                  <div className="flex items-start gap-3">
                    <Check />
                    <p className="text-body-sm text-ink-200 mt-0.5">
                      Full ownership of all code, credentials, and automation flows from day one.
                    </p>
                  </div>
                </SpecimenFrame>
              </div>
            </div>

          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PRIMITIVES                                                      */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionAnchor id="primitives" label="Primitives" />

          <div className="mt-8 space-y-12">

            {/* FAQItem */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                FAQItem · src/components/primitives/FAQItem.jsx
              </p>
              <div className="space-y-3">
                <SpecimenFrame label='defaultOpen={false} · closed state'>
                  <FAQItem
                    q="Do you work with businesses outside Perth?"
                    a="Yes. The work is digital, so geography doesn't matter. We're based in Perth but operate with clients across Australia and internationally."
                  />
                </SpecimenFrame>
                <SpecimenFrame label='defaultOpen={true} · open state'>
                  <FAQItem
                    q="What does a typical engagement look like?"
                    a="Most engagements start with a 45-minute discovery call. From there we scope a fixed-price SOW, build in phases, and hand over full ownership at completion. Retainer support is optional."
                    defaultOpen
                  />
                </SpecimenFrame>
              </div>
            </div>

            {/* ProcessStep */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                ProcessStep · src/components/primitives/ProcessStep.jsx
              </p>
              <p className="text-body-sm text-ink-300 mb-4">
                forwardRef component. Passing no ref is valid; the parent Process section manages refs for the active-step detector.
              </p>
              <div className="space-y-3">
                <SpecimenFrame label='side="left" isActive={false}'>
                  <div className="relative">
                    <ProcessStep
                      index={1}
                      title="Discovery"
                      body="Understand the business, the bottlenecks, and the desired outcomes before writing a line of code."
                      items={['Stakeholder interviews', 'Process mapping', 'Success criteria']}
                      side="left"
                      isActive={false}
                    />
                  </div>
                </SpecimenFrame>
                <SpecimenFrame label='side="right" isActive={true}'>
                  <div className="relative">
                    <ProcessStep
                      index={2}
                      title="Design"
                      body="Architecture decisions made before build so nothing gets rebuilt twice."
                      items={['System design doc', 'Data model', 'Integration plan']}
                      side="right"
                      isActive
                    />
                  </div>
                </SpecimenFrame>
              </div>
            </div>

            {/* MetricCard */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                MetricCard · Metric · src/components/primitives/MetricCard.jsx
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <SpecimenFrame label='status="Shipped" · with Metric'>
                  <MetricCard status="Shipped">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
                        Perth accounting practice
                      </span>
                      <span className="font-mono text-mono-xs tracking-[0.08em] uppercase text-feedback-success">
                        Shipped
                      </span>
                    </div>
                    <div className="mt-6">
                      <Metric value="11 to 1" label="onboarding steps, collapsed to one zero-touch run" />
                    </div>
                    <h3 className="mt-6 text-card-title font-display font-semibold text-ink-50">
                      Lead to signed, without the chase
                    </h3>
                  </MetricCard>
                </SpecimenFrame>
                <SpecimenFrame label='status="In build" · no metric (title-led)'>
                  <MetricCard status="In build">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
                        Sydney clinic group
                      </span>
                      <span className="font-mono text-mono-xs tracking-[0.08em] uppercase text-feedback-warn">
                        In build
                      </span>
                    </div>
                    <h3 className="mt-6 text-display-sm font-display font-semibold text-ink-50">
                      Calls and email, triaged before reception
                    </h3>
                    <p className="mt-3 text-body-sm text-ink-200 m-0">
                      A voice and email layer that routes every inbound message before a person sees it.
                    </p>
                  </MetricCard>
                </SpecimenFrame>
              </div>
            </div>

            {/* HeroCube */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                HeroCube · src/components/primitives/HeroCube.jsx
              </p>
              <SpecimenFrame label='HeroCube · desktop only, cursor-scrubbed video assembly; hidden below lg in Hero usage'>
                <div className="hidden lg:block">
                  <div className="relative h-48 overflow-hidden rounded bg-surface-alt">
                    <HeroCube />
                  </div>
                  <p className="mt-2 font-mono text-mono-xs text-ink-300">
                    Desktop only (hidden below lg). Move the cursor over the video to scrub assembly frames.
                    Static poster shown on coarse/touch pointers and prefers-reduced-motion.
                  </p>
                </div>
                <div className="lg:hidden font-mono text-mono-xs text-ink-300">
                  HeroCube is hidden below lg breakpoint. View on a desktop to see the cursor-scrubbed video.
                </div>
              </SpecimenFrame>
            </div>

            {/* Merlin trigger */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Merlin intake · src/components/primitives/merlin/MerlinCallWidget.jsx + MerlinIframe.jsx
              </p>
              <SpecimenFrame label='MerlinCallWidget is mounted globally by RootLayout · do not remount. It is a plain Link to /intake.'>
                <div className="flex flex-col gap-4">
                  <p className="text-body-sm text-ink-200">
                    The widget FAB is already mounted by{' '}
                    <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">RootLayout</code>,
                    and it — like every Merlin CTA on the site — is a plain router{' '}
                    <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">{'<Link to="/intake">'}</code>.
                    There is no CustomEvent and no modal.{' '}
                    <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">MerlinIframe</code> is used
                    by the <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">/intake</code> page
                    (IntakePage) as a full-bleed embed.
                  </p>
                  <Button variant="primary" size="md" as={Link} to="/intake">
                    Open the intake page
                  </Button>
                </div>
              </SpecimenFrame>
            </div>

            {/* Non-visual primitives */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Non-visual primitives
              </p>
              <SpecimenFrame label='SchemaJsonLd · src/components/primitives/SchemaJsonLd.jsx'>
                <p className="text-body-sm text-ink-200">
                  <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">SchemaJsonLd</code> renders a{' '}
                  <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">{'<script type="application/ld+json">'}</code> tag.
                  It is not mounted here to avoid duplicate schema blocks.
                  Site-global schemas are rendered by <code className="font-mono text-mono-sm bg-white/[.06] px-1 rounded">RootLayout</code>;
                  per-route schemas are inline in each page component.
                </p>
              </SpecimenFrame>
            </div>

          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TERMINAL                                                         */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionAnchor id="terminal" label="Terminal" />

          <div className="mt-8 space-y-12">

            <p className="text-body-sm text-ink-300">
              Live site-wide. A retro-terminal window shell (CRT overlay) wraps the live leak meter in ForWho,
              and the prompt-style eyebrow replaced the dash-line motif across every section. The CRT vocabulary
              is shared with the hero phosphor via the --crt-* variables in terminal.css.
            </p>

            {/* TerminalWindow with leak-meter mock */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                TerminalWindow · src/components/primitives/TerminalWindow.jsx
              </p>
              <SpecimenFrame label='default label · crt-screen effect + leak-meter mock content'>
                <div className="max-w-xl">
                  <TerminalWindow>
                    <div className="flex flex-col gap-3">
                      <span className="font-mono text-mono-xs uppercase tracking-[0.14em] text-ink-300">
                        Leak meter · while you read this
                      </span>
                      <div className="flex items-baseline gap-3">
                        <span className="font-display font-extrabold text-stat tabular-nums text-white">
                          $1,284.50
                        </span>
                        <span className="font-mono text-mono-xs uppercase tracking-[0.14em] text-ink-300">
                          burned
                        </span>
                      </div>
                      <span className="font-mono text-mono-xs text-ink-400">
                        Aggregate of typical operator leaks below. Real-time simulation.
                      </span>
                    </div>
                  </TerminalWindow>
                </div>
              </SpecimenFrame>
            </div>

            {/* PromptEyebrow examples */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                PromptEyebrow · src/components/ui/PromptEyebrow.jsx
              </p>
              <SpecimenFrame label='caret={true} · default — shows blinking caret'>
                <div className="flex flex-col gap-3">
                  <PromptEyebrow>Scaling founders</PromptEyebrow>
                  <PromptEyebrow>Operations leaders</PromptEyebrow>
                  <PromptEyebrow>Enterprise teams</PromptEyebrow>
                  <PromptEyebrow>Work with us</PromptEyebrow>
                  <PromptEyebrow>Start here</PromptEyebrow>
                  <PromptEyebrow>How we work</PromptEyebrow>
                </div>
              </SpecimenFrame>
              <div className="mt-3">
                <SpecimenFrame label='caret={false} · no caret'>
                  <PromptEyebrow caret={false}>Scaling founders</PromptEyebrow>
                </SpecimenFrame>
              </div>
            </div>

            {/* Terminal colour token swatches */}
            <div>
              <p className="font-mono text-mono-xs text-ink-300 uppercase tracking-[0.14em] mb-4">
                Terminal colour tokens
              </p>
              <div className="flex flex-wrap gap-3">
                {(colorGroups['Terminal'] || []).map(([token, hex]) => (
                  <div key={token} className="flex flex-col gap-1.5 w-28">
                    <div
                      className="h-12 rounded border border-white/[.08]"
                      style={{ background: hex }}
                    />
                    <span className="font-mono text-mono-xs text-ink-200 leading-tight break-words">{token}</span>
                    <span className="font-mono text-mono-xs text-ink-300 leading-tight">{hex}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SECTIONS gallery                                                */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <SectionAnchor id="sections" label="Sections gallery" />

          <div className="mt-4">
            <p className="text-body-sm text-ink-200 mb-6">
              Each section below is the real component as used on the site. Nav is a fixed-position element
              already rendered by RootLayout at the top of this page and is not remounted in the gallery.
            </p>

            <div className="font-mono text-mono-xs text-ink-300 mb-2 border border-white/[.04] rounded px-3 py-1.5 inline-block">
              Nav · src/components/sections/Nav.jsx · already mounted by RootLayout above (fixed-position; not remounted here)
            </div>
          </div>
        </div>

        {/* Full-bleed section gallery · outside container-page intentionally */}
        <div>

          <SectionDivider name="Hero" path="src/components/sections/Hero.jsx" />
          <div className="relative">
            <Hero />
          </div>

          <SectionDivider name="ForWho" path="src/components/sections/ForWho.jsx" />
          <ForWho />

          <SectionDivider name="Offers" path="src/components/sections/Offers.jsx" />
          <Offers />

          <SectionDivider name="FlagshipBuilds" path="src/components/sections/FlagshipBuilds.jsx" />
          <FlagshipBuilds />

          <SectionDivider name="HowWeWork" path="src/components/sections/HowWeWork.jsx" />
          <HowWeWork />

          <SectionDivider name="Process" path="src/components/sections/Process.jsx" />
          <Process />

          <SectionDivider name="FAQ" path="src/components/sections/FAQ.jsx" />
          <FAQ />

          <SectionDivider name="CTA" path="src/components/sections/CTA.jsx" />
          <CTA />

          {/* No <Footer /> mount · RootLayout renders the live Footer directly
              after this page, so the divider below labels the real instance. */}
          <SectionDivider name="Footer" path="src/components/sections/Footer.jsx · rendered globally by RootLayout; the footer below is the live instance" />

        </div>
      </div>
    </>
  );
}
